import React from "react";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { Dropdown } from "primereact/dropdown";

import useMyProfile from "./useMyProfile";
import ServiceAccessTable from "./ServiceAccessTable";
import CustomeButton from "../../../components/Button/index";
import timezoneslist from "../../../assets/timezone-list.json";
import { useAuth } from "@workos-inc/authkit-react";
import TimeZoneDropdown from "~/components/TimeZoneSelector";

const MyProfile = () => {
  const { signOut } = useAuth();

  const {
    userdata,
    myentityaccess,
    serviceAvailable,
    paginationFirst,
    paginationRows,
    isUpateTimezone,
    paginationOnchange,
    handleUpdateTimezone,
  } = useMyProfile();

  let role = "Customer User";

  switch (userdata?.role) {
    case "LSP_USER":
      role = "LSP User";
      break;
    case "CUSTOMER":
      role = "Customer User";
      break;
    case "GXM_ADMIN":
      role = "GXM Admin User";
      break;
    case "GXM_USER":
      role = "GXM User";
      break;
    case "GXM_MANAGER":
      role = "GXM Manager";
      break;
    case "LSP_MANAGER":
      role = "LSP Manager";
      break;
    case "CUSTOMER_MANAGER":
      role = "Customer Manager";
      break;
  }

  const handleLogout = () => {
    const returnTo = window.location.origin;
    console.log(`signing out, returning to ${returnTo}`);
    signOut({ returnTo });
  };

  return (
    <>
      <Card className="gen-card h-full">
        <div className="general-info-container flex justify-content-between formgrid grid grid-nogutter">
          <div className="flex-grow">
            <p className="profile-heading">My Account</p>
            <p className="profile-para">
              Manage your personal information and account preferences here.
            </p>
          </div>
          <div className="items-center">
            <CustomeButton
              label="Log Out"
              variant="outlined"
              className="mr-2"
              onClick={handleLogout}
              pi-sign-out
              icon="pi pi-sign-out"
              iconPos="right"
            />
          </div>
          <div className="formgrid grid">
            <div className="field col-12">
              <p className="user-role-heading">General Information</p>
            </div>
            <div className="field col-12 md:col-6 mb-0">
              <label className="input-label">
                Given Name(s) *{" "}
                <i className="pi pi-info-circle" id="given-name-info"></i>
                <Tooltip
                  target="#given-name-info"
                  content="This is usually the person's first name or the name they usually go by."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>

              <InputText
                value={userdata?.firstName}
                placeholder="Given Name"
                name="firstName"
                type="text"
                disabled
                className="appearance-none outline-none w-full disabled-input"
              />
            </div>

            <div className="field col-12 md:col-6 mb-0">
              <label className="input-label">
                Other Name(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="other-name-info"
                ></i>
                <Tooltip
                  target="#other-name-info"
                  content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <InputText
                value={userdata?.other_name}
                placeholder="Other Name"
                name="other_name"
                type="text"
                disabled
                className="appearance-none outline-none w-full disabled-input"
              />
            </div>

            <div className="field col-12 md:col-12 mb-0 mt-3">
              <label className="input-label">
                Surname(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="sur-name-info"
                ></i>
                <Tooltip
                  target="#sur-name-info"
                  content="This is usually a family name or last name. If employee has more than one, include them here."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>

              <InputText
                value={userdata?.last_name}
                placeholder="Enter Surname(s)*"
                name="lastName"
                type="text"
                disabled
                className="appearance-none outline-none w-full disabled-input"
              />
            </div>

            <div className="field col-12 md:col-12 mb-0 mt-3">
              <label className="input-label">
                Preferred name(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="preffered-name-info"
                ></i>
                <Tooltip
                  target="#preffered-name-info"
                  content="If the employee wants to be called by a different name than their given name, put that here."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <InputText
                value={userdata?.preferred_name}
                placeholder="Preferred Name or Nickname"
                name="preferred_name"
                type="text"
                disabled
                className="appearance-none outline-none w-full disabled-input"
              />
            </div>

            <div className="field col-12 md:col-6 mb-0 mt-3">
              <label className="input-label">Email</label>
              <InputText
                value={userdata?.email}
                placeholder="evaccaro@company.com"
                name="email"
                type="text"
                disabled
                className="appearance-none outline-none w-full disabled-input"
              />
            </div>

            <div className="field col-12 md:col-6 mb-0 mt-3">
              <label className="input-label">Organization</label>
              <div className="icon-field">
                <InputText
                  value={userdata?.organization}
                  placeholder="Organisation"
                  type="text"
                  name="organisation"
                  disabled
                  className="appearance-none outline-none w-full disabled-input"
                />
                <i className="pi pi-lock input-icon"></i>
              </div>
            </div>

            <div className="field col-12 md:col-6 mb-0 mt-3">
              <label className="input-label">User Role</label>
              <div className="icon-field">
                <InputText
                  placeholder="User Role"
                  value={role}
                  disabled
                  type="text"
                  name="user_role"
                  className="appearance-none outline-none w-full disabled-input"
                />
                <i className="pi pi-lock input-icon"></i>
              </div>
            </div>

            <div className=" col-12 md:col-12 mt-2">
              <p className="location-heading">Location and Time Zone</p>
            </div>
            <div className="field col-12 md:col-6 mb-2">
              <label className="input-label">
                Time Zone *{" "}
                <i className="pi pi-info-circle" id="given-name-info"></i>
                <Tooltip
                  target="#given-name-info"
                  content="Select your time zone for accurate scheduling and project timing."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <div className="icon-field">
                <TimeZoneDropdown
                  timezones={timezoneslist}
                  value={userdata?.timezone}
                  onChange={handleUpdateTimezone}
                  loading={isUpateTimezone}
                  disabled={isUpateTimezone}
                />
              </div>
            </div>
          </div>

          <ServiceAccessTable
            entityaccess={myentityaccess}
            services={serviceAvailable}
            paginationFirst={paginationFirst}
            paginationRows={paginationRows}
            paginationOnchange={paginationOnchange}
          />
        </div>
      </Card>
    </>
  );
};

export default MyProfile;
